import { useCallback, useEffect, useState } from "react";

// The "modern" look for the Be-Measured report, ported from the client app
// (bemeasured/src/theme). Same stylesheet, same toggle, same tabs and
// sections. One difference: in the admin panel the look applies ONLY while
// the report page is mounted - the stylesheet restyles the header and the
// page background, and nothing else in the admin has been designed for it.
// The choice is still remembered per browser (localStorage "kit-theme") and
// a link can carry it (?look=modern|classic), so it comes back next visit.
export type KitTheme = "classic" | "modern";

const STORAGE_KEY = "kit-theme";
const ATTRIBUTE = "data-kit-theme";
const FONT_LINK_ID = "kit-theme-font";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
const REPORT_PATH = /^\/be-measured(\/|$)/;

// The old look and its switch were retired 2026-10-14 (George): modern always applies, whatever an
// earlier visit stored or a ?look= link asks for.
let current: KitTheme = "modern";
// True while the report page is mounted (seeded from the URL so a direct
// load of /be-measured?look=modern paints modern before React mounts).
let inScope = REPORT_PATH.test(window.location.pathname);
const listeners = new Set<() => void>();

const render = () => {
  const root = document.documentElement;
  if (current === "modern" && inScope) {
    root.setAttribute(ATTRIBUTE, "modern");
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  } else {
    root.removeAttribute(ATTRIBUTE);
  }
  listeners.forEach((fn) => fn());
};

export const applyKitTheme = (theme: KitTheme) => {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    // private mode etc. - the choice just won't persist
  }
  current = theme;
  render();
};

// Called by the report container on mount/unmount.
export const setKitThemeScope = (active: boolean) => {
  inScope = active;
  render();
};

// Apply the stored choice as soon as the bundle loads, before first paint.
render();

export const useKitTheme = () => {
  const [, bump] = useState(0);

  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const toggle = useCallback(() => {
    applyKitTheme(current === "modern" ? "classic" : "modern");
  }, []);

  return {
    theme: current,
    toggle,
    isModern: current === "modern" && inScope,
    inScope,
  };
};
